import { ActionContext } from "vuex";
import {
  Menu,
  TrainingCourse,
  TrainingCourseRequest
} from "../../../../domain/entities/courseEvent/training_course";
import trainingCourseUseCases from "../../../../domain/usecase/impl/training-course/trainingCourseUseCases";
import {
  AttendanceAt,
  Instructor,
  User
} from "../../../../domain/entities/entities";
import store from "../store";
import { dateGrouped } from "../../../presentation/components/mixins";

class TrainingCourseState {
  trainingCourse: TrainingCourse = new TrainingCourse();
  instructors: Instructor[] = [];
  enrolledUsers: User[] = [];
  isUserEnrolled = false;
  isUserAttendance = false;
  menuIDQrCode = 0;
  currentMenu: Menu = new Menu();
  userPercentage = 0;
}

const state = new TrainingCourseState();

const mutations = {
  SET_TRAINING_COURSE(
    state: TrainingCourseState,
    trainingCourse: TrainingCourse
  ): void {
    state.trainingCourse = trainingCourse;
  },

  SET_INSTRUCTORS(state: TrainingCourseState, instructors: Instructor[]): void {
    state.instructors = instructors;
  },

  SET_ENROLL_USERS(state: TrainingCourseState, users: User[]): void {
    state.enrolledUsers = users;
  },

  SET_IS_USER_ENROLLED(
    state: TrainingCourseState,
    isUserEnrolled: boolean
  ): void {
    state.isUserEnrolled = isUserEnrolled;
  },

  SET_IS_USER_ATTENDANCE(
    state: TrainingCourseState,
    isUserAttendace: boolean
  ): void {
    state.isUserAttendance = isUserAttendace;
  },
  SET_MENU_ID_QR_CODE(state: TrainingCourseState, id: number): void {
    state.menuIDQrCode = id;
  },
  SET_CURRENT_MENU(state: TrainingCourseState, menu: Menu): void {
    state.currentMenu = menu;
  },
  SET_USER_PERCENTAGE(state: TrainingCourseState, percentage: number): void {
    state.userPercentage = percentage;
  }
};

const actions = {
  /** Gets a training course.
   * @param commit
   * @param id ID of the course. */
  async get(
    { commit }: ActionContext<TrainingCourseState, TrainingCourseState>,
    id: number
  ): Promise<void> {
    try {
      const response: TrainingCourseRequest = await trainingCourseUseCases.get(
        id
      );
      commit("SET_TRAINING_COURSE", response.graduation);
      commit("SET_ENROLL_USERS", response.users);

      /** Checks if the user is enrolled/attendance. */
      const user: User = await store.getters["userProfile_module/user"];
      if (!response.users) return;

      const isUserEnrolledForEvent = response.users.find(
        enrolledUser => enrolledUser.id === user.id
      );
      if (!isUserEnrolledForEvent) return;

      const userPercentage = isUserEnrolledForEvent.percentageUser || 0;
      commit("SET_USER_PERCENTAGE", userPercentage);

      commit("SET_IS_USER_ENROLLED", true);

      const menuIDQrCode = state.menuIDQrCode;

      if (menuIDQrCode) {
        const attendanceDay = isUserEnrolledForEvent.attendanceAts?.filter(
          (attendanceAt: AttendanceAt) => {
            return attendanceAt.id === menuIDQrCode;
          }
        );

        const currentMenu = response.graduation.menus.filter((menu: Menu) => {
          return menu.id === menuIDQrCode;
        });

        if (attendanceDay && attendanceDay.length) {
          commit("SET_IS_USER_ATTENDANCE", true);
        }

        if (currentMenu.length) {
          commit("SET_CURRENT_MENU", currentMenu[0]);
        }
      }
    } catch (e) {
      console.log(e);
      throw e;
    }
  },

  async getInstructors(
    { commit }: ActionContext<TrainingCourseState, TrainingCourseState>,
    id: number
  ): Promise<void> {
    try {
      const response: Instructor[] = await trainingCourseUseCases.getInstructors(
        id
      );
      commit("SET_INSTRUCTORS", response);
    } catch (e) {
      console.log(e);
      throw e;
    }
  },

  async enroll(
    { commit }: ActionContext<TrainingCourseState, TrainingCourseState>,
    id: number
  ): Promise<void> {
    try {
      await trainingCourseUseCases.enroll(id);
      commit("SET_IS_USER_ENROLLED", true);
    } catch (e) {
      console.log(e);
      throw e;
    }
  },

  async unenroll(
    { commit }: ActionContext<TrainingCourseState, TrainingCourseState>,
    id: number
  ): Promise<void> {
    try {
      commit("SET_IS_USER_ENROLLED", false);
      return await trainingCourseUseCases.unenroll(id);
    } catch (e) {
      console.log(e);
      throw e;
    }
  },

  async confirmCheckinQrCode(
    { commit }: ActionContext<TrainingCourseState, TrainingCourseState>,
    payload: { token: string; id: number }
  ): Promise<void> {
    const response = await trainingCourseUseCases.confirmCheckinQrCode(
      payload.token,
      payload.id
    );
    commit("SET_IS_USER_ENROLLED", true);
    commit("SET_IS_USER_ATTENDANCE", true);
    return response;
  },

  async setMenuIDQrCode(
    { commit }: ActionContext<TrainingCourseState, TrainingCourseState>,
    id: number
  ): Promise<void> {
    commit("SET_MENU_ID_QR_CODE", id);
  }
};

const getters = {
  trainingCourse: (state: TrainingCourseState): TrainingCourse =>
    state.trainingCourse,
  instructors: (state: TrainingCourseState): Instructor[] => state.instructors,
  enrolledUsers: (state: TrainingCourseState): User[] => state.enrolledUsers,
  isUserEnrolled: (state: TrainingCourseState): boolean => state.isUserEnrolled,
  isUserAttendance: (state: TrainingCourseState): boolean =>
    state.isUserAttendance,
  currentMenu: (state: TrainingCourseState): Menu => state.currentMenu,
  userPercentage: (state: TrainingCourseState): number => state.userPercentage
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
